/** biome-ignore-all lint/complexity/noUselessFragments: Fragment is required to group multiple children inside GoogleMap without adding an extra DOM node. */
import type { TrackingGraphQL } from '@cobliteam/api-lib';
import Environment from '@cobliteam/react-environments';
import Loading from '@cobliteam/ui/components/Loading';
import Typography from '@cobliteam/ui/components/Typography';
import { cn } from '@cobliteam/ui/utils';
import { APIProvider, ControlPosition, Map as GoogleMap, MapControl } from '@vis.gl/react-google-maps';
import { FullScreen, useFullScreenHandle } from 'react-full-screen';
import TrackingMapFullScreen from './TrackingMapFullScreen';
import TrackingMapPaths from './TrackingMapPaths';
import TrackingMapZoom, { MAX_ZOOM, MIN_ZOOM } from './TrackingMapZoom';

const env = Environment.getInstance().getEnvironment();

interface TrackingMapProps {
  tracking?: TrackingGraphQL;
  pathWindow: {
    start: number;
    end?: number;
  };
  loading?: boolean;
  className?: string;
}

const DEFAULT_CENTER = { lat: -23.5505, lng: -46.6333 };
const DEFAULT_ZOOM = 12;

const TrackingMap = ({ tracking, pathWindow, loading, className }: TrackingMapProps) => {
  const fullscreenHandler = useFullScreenHandle();

  return (
    <APIProvider apiKey={env.googleMapsKey || ''}>
      <FullScreen handle={fullscreenHandler}>
        <GoogleMap
          mapId='media-request-map'
          defaultCenter={DEFAULT_CENTER}
          defaultZoom={DEFAULT_ZOOM}
          minZoom={MIN_ZOOM}
          maxZoom={MAX_ZOOM}
          disableDefaultUI
          className={cn('relative h-64 w-full overflow-hidden rounded-lg', className, fullscreenHandler.active && 'size-full')}
        >
          <>
            {/* Render tracking path polylines and markers */}
            {tracking && <TrackingMapPaths path={tracking.pathGeojson} pathWindow={pathWindow} />}

            {/* Render map controls */}
            <MapControl position={ControlPosition.TOP_LEFT}>
              <div className='m-4 flex flex-col gap-2'>
                <TrackingMapZoom />
                <TrackingMapFullScreen fullScreenHandler={fullscreenHandler} />
              </div>
            </MapControl>

            {/* Render loading state */}
            {loading && (
              <div className='absolute inset-0 flex flex-col items-center justify-center bg-black-a7 [&_g]:fill-white'>
                <Loading />
                <Typography variant='body' color='white'>
                  Carregando mapa...
                </Typography>
              </div>
            )}
          </>
        </GoogleMap>
      </FullScreen>
    </APIProvider>
  );
};

export default TrackingMap;
